import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@/components/ui/select';
import { Tag, X } from 'lucide-react';
import { getStatusStyle } from '@/lib/capability-colors';
import { useCapabilityUpdate } from '@/hooks/use-entity-mutations';
import { toast } from 'sonner';
import { VISIBILITY_OPTIONS, type CapabilityVisibility } from '@/components/ui/visibility-select';
import { normalizeCapabilityVisibility } from '@/lib/capability-visibility';

const LOG_PREFIX = '[CapabilityOverviewTab]';

function statusApiValue(selectValue: string): string {
   return selectValue === 'paused' ? 'Paused' : 'Active';
}

export type OverviewPersistMode = 'immediate' | 'deferred';

export type DeferredOverviewDirtyPayload = {
   dirty: boolean;
   patch: Record<string, unknown>;
};

interface CapabilityOverviewProps {
   data: {
      id: string;
      app_key: string;
      name: string;
      display_name?: string | null;
      fqcn?: string;
      type: string;
      description?: string | null;
      aliases?: string[];
      tags?: string[];
      status: string;
      visibility?: string;
      domain?: string | null;
      is_system?: boolean;
      revision?: number;
      created?: string;
      modified?: string;
   };
   /** When `deferred`, status/visibility/tags stay local until the parent saves (e.g. agent header Save). */
   persistMode?: OverviewPersistMode;
   onDeferredDirtyChange?: (payload: DeferredOverviewDirtyPayload) => void;
   /**
    * Parent-owned draft from a previous edit session. Restored when the settings
    * sheet remounts so closing the drawer does not discard visibility/status/tags.
    */
   pendingPatch?: Record<string, unknown>;
   /** Disable editable controls (e.g. while parent is saving). */
   formDisabled?: boolean;
}

function tagsFromData(tags: string[] | undefined): string[] {
   return Array.isArray(tags) ? [...tags] : [];
}

function statusSelectFromData(status: string | undefined): 'active' | 'paused' {
   return (status ?? '').toLowerCase() === 'paused' ? 'paused' : 'active';
}

function buildDeferredOverviewPatch(args: {
   data: CapabilityOverviewProps['data'];
   tags: string[];
   deferredStatus: 'active' | 'paused';
   deferredVisibility: CapabilityVisibility;
   displayNameDraft: string;
   descriptionDraft: string;
}): Record<string, unknown> {
   const patch: Record<string, unknown> = {};
   const dataStatusNorm = statusSelectFromData(args.data.status);
   if (args.deferredStatus !== dataStatusNorm) {
      patch.status = statusApiValue(args.deferredStatus);
   }
   const dataVis = normalizeCapabilityVisibility(args.data.visibility);
   if (args.deferredVisibility !== dataVis) {
      patch.visibility = args.deferredVisibility;
   }
   const dataTags = tagsFromData(args.data.tags);
   const a = [...args.tags].sort().join('\n');
   const b = [...dataTags].sort().join('\n');
   if (a !== b) {
      patch.tags = [...args.tags];
   }
   const serverDisplay = (args.data.display_name ?? '').trim();
   if (args.displayNameDraft.trim() !== serverDisplay) {
      patch.display_name = args.displayNameDraft.trim() || null;
   }
   const serverDesc = args.data.description ?? '';
   if (args.descriptionDraft !== serverDesc) {
      patch.description = args.descriptionDraft || null;
   }
   return patch;
}

function draftFromDataAndPending(
   data: CapabilityOverviewProps['data'],
   pendingPatch: Record<string, unknown> | undefined,
): {
   tags: string[];
   deferredStatus: 'active' | 'paused';
   deferredVisibility: CapabilityVisibility;
   displayNameDraft: string;
   descriptionDraft: string;
} {
   const pendingTags = pendingPatch?.tags;
   const pendingStatus = pendingPatch?.status;
   const pendingVisibility = pendingPatch?.visibility;
   const pendingDisplayName = pendingPatch?.display_name;
   const pendingDescription = pendingPatch?.description;

   return {
      tags: Array.isArray(pendingTags)
         ? [...(pendingTags as string[])]
         : tagsFromData(data.tags),
      deferredStatus:
         pendingStatus !== undefined
            ? statusSelectFromData(String(pendingStatus))
            : statusSelectFromData(data.status),
      deferredVisibility:
         pendingVisibility !== undefined
            ? normalizeCapabilityVisibility(String(pendingVisibility))
            : normalizeCapabilityVisibility(data.visibility),
      displayNameDraft:
         pendingDisplayName !== undefined
            ? String(pendingDisplayName ?? '').trim()
            : (data.display_name?.trim() ?? ''),
      descriptionDraft:
         pendingDescription !== undefined
            ? String(pendingDescription ?? '')
            : (data.description ?? ''),
   };
}

export function CapabilityOverviewTab({
   data,
   persistMode = 'immediate',
   onDeferredDirtyChange,
   pendingPatch,
   formDisabled = false,
}: CapabilityOverviewProps) {
   const { appKey, name } = useParams();
   const resolvedAppKey = appKey ?? data.app_key ?? '';
   const resolvedName = name ?? data.name ?? '';
   const updateMutation = useCapabilityUpdate(resolvedAppKey, resolvedName);
   const isDeferred = persistMode === 'deferred';

   const [tags, setTags] = useState<string[]>(() =>
      draftFromDataAndPending(data, isDeferred ? pendingPatch : undefined).tags,
   );
   const [tagInput, setTagInput] = useState('');
   const [displayNameDraft, setDisplayNameDraft] = useState(
      () => draftFromDataAndPending(data, isDeferred ? pendingPatch : undefined).displayNameDraft,
   );
   const [descriptionDraft, setDescriptionDraft] = useState(
      () => draftFromDataAndPending(data, isDeferred ? pendingPatch : undefined).descriptionDraft,
   );
   const [deferredStatus, setDeferredStatus] = useState<'active' | 'paused'>(
      () => draftFromDataAndPending(data, isDeferred ? pendingPatch : undefined).deferredStatus,
   );
   const [deferredVisibility, setDeferredVisibility] = useState<CapabilityVisibility>(
      () => draftFromDataAndPending(data, isDeferred ? pendingPatch : undefined).deferredVisibility,
   );

   const serverSnapshot = useMemo(
      () =>
         JSON.stringify({
            tags: tagsFromData(data.tags),
            status: statusSelectFromData(data.status),
            visibility: normalizeCapabilityVisibility(data.visibility),
            display_name: (data.display_name ?? '').trim(),
            description: data.description ?? '',
         }),
      [data.tags, data.status, data.visibility, data.display_name, data.description],
   );

   const pendingKey = useMemo(
      () => JSON.stringify(pendingPatch ?? {}),
      [pendingPatch],
   );
   const hasPendingDraft = !!pendingPatch && Object.keys(pendingPatch).length > 0;
   const prevServerSnapshotRef = useRef(serverSnapshot);
   const prevPendingKeyRef = useRef(pendingKey);

   // Immediate mode: mirror server fields.
   // Deferred mode: only adopt server/pending when those baselines change — never
   // reset local edits just because the parent re-rendered.
   useEffect(() => {
      if (!isDeferred) {
         setTags(tagsFromData(data.tags));
         setDisplayNameDraft(data.display_name?.trim() ?? '');
         setDescriptionDraft(data.description ?? '');
         return;
      }

      const serverChanged = prevServerSnapshotRef.current !== serverSnapshot;
      const pendingChanged = prevPendingKeyRef.current !== pendingKey;
      prevServerSnapshotRef.current = serverSnapshot;
      prevPendingKeyRef.current = pendingKey;

      if (!serverChanged && !pendingChanged) return;

      if (hasPendingDraft) {
         const restored = draftFromDataAndPending(data, pendingPatch);
         setTags(restored.tags);
         setDeferredStatus(restored.deferredStatus);
         setDeferredVisibility(restored.deferredVisibility);
         setDisplayNameDraft(restored.displayNameDraft);
         setDescriptionDraft(restored.descriptionDraft);
         return;
      }

      if (serverChanged) {
         const restored = draftFromDataAndPending(data, undefined);
         setTags(restored.tags);
         setDeferredStatus(restored.deferredStatus);
         setDeferredVisibility(restored.deferredVisibility);
         setDisplayNameDraft(restored.displayNameDraft);
         setDescriptionDraft(restored.descriptionDraft);
      }
   }, [isDeferred, serverSnapshot, pendingKey, hasPendingDraft, data, pendingPatch]);

   const emitDeferred = useCallback(() => {
      if (!isDeferred || !onDeferredDirtyChange) return;
      const patch = buildDeferredOverviewPatch({
         data,
         tags,
         deferredStatus,
         deferredVisibility,
         displayNameDraft,
         descriptionDraft,
      });
      onDeferredDirtyChange({ dirty: Object.keys(patch).length > 0, patch });
   }, [
      isDeferred,
      onDeferredDirtyChange,
      data,
      tags,
      deferredStatus,
      deferredVisibility,
      displayNameDraft,
      descriptionDraft,
   ]);

   useEffect(() => {
      emitDeferred();
   }, [emitDeferred]);

   const statusStyle = getStatusStyle(data.status ?? '');
   const visibilityValue = isDeferred
      ? deferredVisibility
      : normalizeCapabilityVisibility(data.visibility);
   const statusLower = (data.status ?? '').toLowerCase();
   const isArchived = statusLower === 'archived';
   const statusSelectValue = isDeferred ? deferredStatus : statusSelectFromData(data.status);
   /** In deferred mode saves go through header Save; do not block edits while a mutation is in flight. */
   const controlsLocked =
      !resolvedAppKey ||
      !resolvedName ||
      (!isDeferred && updateMutation.isPending) ||
      isArchived ||
      formDisabled;
   const tagsDisabled = controlsLocked;
   const selectDisabled = controlsLocked;

   async function patchCapability(body: Record<string, unknown>) {
      if (!resolvedAppKey || !resolvedName) return;
      try {
         await updateMutation.mutateAsync(body);
         toast.success('Capability updated');
      } catch {
         console.error(`${LOG_PREFIX}.patchCapability: request failed`, body);
         toast.error('Update failed', { description: 'Could not save changes. Try again.' });
      }
   }

   async function persistTags(next: string[]) {
      if (!resolvedAppKey || !resolvedName) return;
      const previous = [...tags];
      setTags(next);
      try {
         await updateMutation.mutateAsync({ tags: next });
         toast.success('Capability updated');
      } catch {
         console.error(`${LOG_PREFIX}.persistTags: request failed`, { tags: next });
         setTags(previous);
         toast.error('Update failed', { description: 'Could not save tags. Try again.' });
      }
   }

   function handleTagsAdd(raw: string) {
      const t = raw.trim();
      if (!t || tags.includes(t)) return;
      if (isDeferred) {
         setTags([...tags, t]);
      } else {
         void persistTags([...tags, t]);
      }
      setTagInput('');
   }

   function persistDisplayNameAndDescriptionIfImmediate() {
      if (isDeferred || controlsLocked) return;
      const serverDisplay = (data.display_name ?? '').trim();
      const serverDesc = data.description ?? '';
      const nextDisplay = displayNameDraft.trim();
      const patch: Record<string, unknown> = {};
      if (nextDisplay !== serverDisplay) {
         patch.display_name = nextDisplay || null;
      }
      if (descriptionDraft !== serverDesc) {
         patch.description = descriptionDraft || null;
      }
      if (Object.keys(patch).length > 0) {
         void patchCapability(patch);
      }
   }

   function handleTagsRemove(tag: string) {
      if (isDeferred) {
         setTags(tags.filter((x) => x !== tag));
      } else {
         void persistTags(tags.filter((x) => x !== tag));
      }
   }

   return (
      <div className="flex flex-col gap-6 rounded-xl border border-slate-200 bg-card p-6 text-card-foreground shadow-sm dark:border-slate-700">
            <div className="flex w-full min-w-0 flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
               <div className="flex min-w-0 flex-1 flex-col gap-4">
                  <div className="space-y-2">
                     <Label htmlFor="cap-overview-name">Capability Name</Label>
                     <Input
                        id="cap-overview-name"
                        readOnly
                        value={data.name}
                        className="w-full bg-muted font-mono"
                     />
                  </div>
                  <div className="space-y-2">
                     <Label htmlFor="cap-overview-display-name">Display name</Label>
                     <Input
                        id="cap-overview-display-name"
                        value={displayNameDraft}
                        disabled={controlsLocked}
                        onChange={(e) => setDisplayNameDraft(e.target.value)}
                        onBlur={() => persistDisplayNameAndDescriptionIfImmediate()}
                        placeholder="—"
                        className="w-full"
                     />
                  </div>
               </div>

               <div className="flex min-w-0 flex-1 flex-col gap-4">
                  <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-start">
                     <div className="w-full shrink-0 space-y-2 sm:w-44 md:w-48">
                        <Label htmlFor="cap-overview-status">Status</Label>
                        {isArchived ? (
                           <div className="flex h-9 items-center">
                              <span
                                 className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusStyle}`}
                              >
                                 {data.status}
                              </span>
                           </div>
                        ) : (
                           <Select
                              value={statusSelectValue}
                              disabled={selectDisabled}
                              onValueChange={(v) => {
                                 if (isDeferred) {
                                    setDeferredStatus(v as 'active' | 'paused');
                                 } else {
                                    void patchCapability({ status: statusApiValue(v) });
                                 }
                              }}
                           >
                              <SelectTrigger id="cap-overview-status" className="w-full">
                                 <SelectValue placeholder="Status" />
                              </SelectTrigger>
                              <SelectContent>
                                 <SelectItem value="active">Active</SelectItem>
                                 <SelectItem value="paused">Paused</SelectItem>
                              </SelectContent>
                           </Select>
                        )}
                     </div>
                     <div className="w-full shrink-0 space-y-2 sm:w-44 md:w-48">
                        <Label htmlFor="cap-overview-visibility">Visibility</Label>
                        <Select
                           value={visibilityValue}
                           disabled={selectDisabled}
                           onValueChange={(v) => {
                              if (isDeferred) {
                                 setDeferredVisibility(v as CapabilityVisibility);
                              } else {
                                 void patchCapability({ visibility: v });
                              }
                           }}
                        >
                           <SelectTrigger id="cap-overview-visibility" className="w-full">
                              <SelectValue placeholder="Visibility" />
                           </SelectTrigger>
                           <SelectContent>
                              {VISIBILITY_OPTIONS.map((opt) => (
                                 <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                 </SelectItem>
                              ))}
                           </SelectContent>
                        </Select>
                     </div>
                     <div className="w-full shrink-0 space-y-2 sm:w-44 md:w-48">
                        <Label htmlFor="cap-overview-revision">Revision</Label>
                        <Input
                           id="cap-overview-revision"
                           readOnly
                           value={typeof data.revision === 'number' ? String(data.revision) : ''}
                           placeholder="—"
                           className="w-full bg-muted"
                        />
                     </div>
                  </div>

                  <div className="min-w-0 space-y-2">
                     <Label htmlFor="cap-overview-tags">Tags</Label>
                     <div className="flex flex-wrap gap-2">
                        <Input
                           id="cap-overview-tags"
                           value={tagInput}
                           disabled={tagsDisabled}
                           onChange={(e) => setTagInput(e.target.value)}
                           onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                 e.preventDefault();
                                 handleTagsAdd(tagInput);
                              }
                           }}
                           placeholder="Add a tag and press Enter"
                           className="min-w-0 max-w-full flex-1 sm:max-w-108"
                        />
                        <Button
                           type="button"
                           variant="outline"
                           size="sm"
                           disabled={tagsDisabled || !tagInput.trim()}
                           onClick={() => handleTagsAdd(tagInput)}
                        >
                           Add
                        </Button>
                     </div>
                     {tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                           {tags.map((tag) => (
                              <Badge
                                 key={tag}
                                 variant="secondary"
                                 className="gap-1 border-transparent bg-amber-50 text-xs text-amber-900 dark:bg-amber-950/50 dark:text-amber-100"
                              >
                                 <Tag size={10} />
                                 {tag}
                                 <button
                                    type="button"
                                    disabled={tagsDisabled}
                                    onClick={() => handleTagsRemove(tag)}
                                    className="ml-0.5 rounded p-0.5 hover:bg-amber-200/80 disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-amber-900/60"
                                    aria-label={`Remove tag ${tag}`}
                                 >
                                    <X size={10} />
                                 </button>
                              </Badge>
                           ))}
                        </div>
                     ) : (
                        <p className="text-muted-foreground text-sm">No tags</p>
                     )}
                  </div>
               </div>
            </div>

            <div className="space-y-2">
               <Label htmlFor="cap-overview-description">Description</Label>
               <Textarea
                  id="cap-overview-description"
                  value={descriptionDraft}
                  disabled={controlsLocked}
                  onChange={(e) => setDescriptionDraft(e.target.value)}
                  onBlur={() => persistDisplayNameAndDescriptionIfImmediate()}
                  placeholder="No description"
                  rows={5}
                  className="min-h-24 resize-none"
               />
            </div>

            {(data.created || data.modified) && (
               <div className="grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                  {data.created && (
                     <div>Created: {new Date(data.created).toLocaleString()}</div>
                  )}
                  {data.modified && (
                     <div>Modified: {new Date(data.modified).toLocaleString()}</div>
                  )}
               </div>
            )}
      </div>
   );
}
